import { Grid, useTheme, Text, Box, Heading } from '@simplywallst/ui-core';
import {
  CriteriaChecksWrapper,
  ShowMoreButton,
  ShowMoreIcon,
  StatmentChecksWrapper,
  CriteriaCheckRow,
  SnowflakeWrapper,
  SnowflakeContainer,
  HiddenContent,
} from './styled';
import Score from '@/components/Score';
import { useCallback, useMemo, useState } from 'react';
import { useTracking } from 'react-tracking';
import loadable from '@loadable/component';
import type { AnalysisCheck } from '@/pages/CompanyReport/redux/interface';
import { Transition } from 'react-transition-group';
import { useSummary } from '@/hooks/useCompany';
import { SnowflakeType } from '@/components/DeprecatedSnowflake/SnowflakeType';
import { useReportSection } from '../../../ReportSectionProvider';
import { mapReportSectionToSnowflakeSection } from '../../../Overview/components/IntroductionDeprecated/IntroductionDeprecated';
import useIntroSentence from '../../hooks/useIntroSentence';
import type { StatementChecks as Checks } from '../../utils';
import { slugify } from '@/utilities/slugify';
import { useTranslation } from '@/hooks/useTranslation';
import type { HeadingElements } from '@/utilities/heading';

const StatementChecks = loadable(
  () =>
    import(
      '@/pages/CompanyReport/components/Overview/components/IntroductionDeprecated/components/StatementChecks'
    ),
  {
    ssr: false,
  }
);

const Snowflake = loadable(() => import('@/components/DeprecatedSnowflake'), {
  ssr: false,
});

interface Props {
  scoreName: string;
  checks: AnalysisCheck[] | Checks[];
  showSnowflake?: boolean;
  titleElement?: HeadingElements;
}
const CriteriaChecks = ({
  scoreName,
  checks,
  showSnowflake = true,
  titleElement = 'h3',
}: Props) => {
  const ANIMATION_DURATION = 300;
  const { section } = useReportSection();
  const introSentence = useIntroSentence(section);
  const snowflakeSection =
    section && mapReportSectionToSnowflakeSection(section);
  const summary = useSummary();
  const [open, setOpen] = useState(false);
  const [contentHidden, setContentHidden] = useState(true);
  const theme = useTheme();
  const score = useMemo(
    () => checks.filter((check) => check.mark === 'PASS').length,
    [checks]
  );
  const { trackEvent } = useTracking();
  const onClickShowMore = useCallback(() => {
    const expectedOpen = !open;
    trackEvent({
      modifier: 'intro',
      action: expectedOpen ? 'open' : 'close',
    });
    setOpen(expectedOpen);
  }, [trackEvent, open]);

  const t = useTranslation();

  return (
    <CriteriaChecksWrapper
      open={open}
      column="span 1"
      columnMd="span 2"
      gap={`${theme.x2spacePx}px`}
      gapMd={`${theme.x1spacePx}px`}
      templateColumns="auto 40px"
      templateRows="auto auto"
      className="bg-surface-2 print:bg-p-white"
    >
      <CriteriaCheckRow>
        <Box marginRightMd="x2">
          <Heading
            data-cy-id={`${scoreName}-criteria-checks`}
            as={titleElement}
            typography="small"
          >
            {t('page.companyReport.criteriaCheck', {
              scoreName,
              score,
              scoreTotal: checks.length > 0 ? checks.length : 6,
            })}
          </Heading>
        </Box>
        <Score score={score} values={checks} />
      </CriteriaCheckRow>
      <ShowMoreButton
        data-cy-id="criteria-check-show-more"
        styleType="loudLarge"
        onClick={onClickShowMore}
      >
        <ShowMoreIcon open={open} />
      </ShowMoreButton>
      <Grid column="span 2">
        <Text
          data-cy-id={`${scoreName}-intro-sentence`}
          typography="xSmall"
          color={'whiteRGBA'}
          opacity={0.8}
          lineClamp={contentHidden ? 2 : 10}
        >
          {introSentence}
        </Text>
      </Grid>
      <Transition
        in={open}
        timeout={ANIMATION_DURATION}
        onEnter={() => setContentHidden(false)}
        onExited={() => setContentHidden(true)}
      >
        <HiddenContent
          column="span 2"
          templateColumnsMd="1fr 1fr"
          justifyContentMd="space-between"
          alignContent="center"
          alignItems="center"
          justifyItems="start"
          rowGap={`${theme.x5spacePx}px`}
          open={!contentHidden}
        >
          <StatmentChecksWrapper>
            <StatementChecks
              title={`${scoreName} Score`}
              testId={`${slugify(scoreName)}-score`}
              checks={checks}
              score={score}
            />
          </StatmentChecksWrapper>
          {showSnowflake && (
            <SnowflakeContainer justifyContent="center" alignItems="center">
              <SnowflakeWrapper>
                {summary.status === 'success' ? (
                  <Snowflake
                    points={summary.score}
                    tooltip={false}
                    type={SnowflakeType.TOC}
                    highlightSection={snowflakeSection}
                    width={200}
                  />
                ) : null}
              </SnowflakeWrapper>
            </SnowflakeContainer>
          )}
        </HiddenContent>
      </Transition>
    </CriteriaChecksWrapper>
  );
};

export default CriteriaChecks;
